import { qfetch, constants } from "@qgiv/core-js";

/**
 * @public
 * @function getSubmitConfirmationFieldsUrl
 * @description helper function to create the submit api url for confirmation fields
 * @param {string} formId id of the form being submitted
 * @returns {string} submitConfirmationFields api url
 */
const getSubmitConfirmationFieldsUrl = (formId) =>
    `/api/v1/${formId}/submit_confirmation_fields`;

/**
 * @public
 * @function getSubmitUrl
 * @description helper function to create the submit api url for events
 * @returns {string} submit api url
 */
export const getSubmitUrl = () => "/api/v1/submit";

export const getValidateAbandonedGiftTokenUrl = (formId) =>
    `/api/v1/formresources/form/${formId}/abandonedgifts/details/validateAbandonedGiftToken`;

export const getSubmitAbandonedGiftDataUrl = (formId) =>
    `/api/v1/formresources/form/${formId}/abandonedgifts/details/`;

export const getScheduleAbandonedGiftEmailsUrl = (formId) =>
    `/api/v1/formresources/form/${formId}/scheduledemails`;

export const getSubmitAbandonedGiftTrackingDataUrl = (formId) =>
    `/api/v1/formresources/form/${formId}/abandonedgifts/tracking`;

export const getCancelRecurringPaymentUrl = (recurringId) =>
    `/api/v1/${recurringId}/cancel_recurring_payment`;

export const getValidateDonationUrl = () =>
    "api/v1/donationform/onepay/validate_onepay_submission";

/**
 * @public
 * @function submitDonationAPI
 * @description API call for submitting a donation
 * @param {object} data return of prepareValuesForEventSubmit
 * @returns {Promise} axios promise by way of qfetch
 */
export const submitDonationAPI = (data = {}) =>
    qfetch.post(getSubmitUrl(), data);

/*
    $this->searchCategory = $request['searchCategory'];
    $this->formId = (int)$request['formId'];
    $this->searchTerm = $request['searchTerm'] ?? '';
    $this->globalSearch = $request['globalSearch'] ?? 0;
    $this->source = $request['source'] ?? '';
    $this->parentOnly = $request['parentOnly'] ?? 0;
    $this->recentParticipants = $request['recentParticipants'] ?? 0;
*/
export const getRecipientList = (params = {}) =>
    qfetch.get("/api/v1/donationform/donationrecipient/search", { params });

export const submitConfirmationFields = (formId, data) =>
    qfetch.post(getSubmitConfirmationFieldsUrl(formId), data);

export const submitCancelRecurringPayment = (recurringId) =>
    qfetch.post(getCancelRecurringPaymentUrl(recurringId));

/**
 * @description Validates the abandoned gift token for a specific form and recipient email.
 * @param {object} params
 * @param {number} params.formId
 * @param {string} params.recipientEmail
 * @param {string} params.token - The abandoned gift token.
 * @returns {Promise} axios promise by way of qfetch
 */
export const validateAbandonedGiftTokenAPI = ({
    formId,
    recipientEmail,
    token,
}) => {
    const url = getValidateAbandonedGiftTokenUrl(formId);
    return qfetch.post(url, { token, recipientEmail });
};

/**
 * @description Sends form data to BE to store so we can load previous data when returning from email
 * @param {object} params
 * @param {object} params.abandonedGiftData
 * @param {number} params.formId
 * @param {string} params.token - The abandoned gift token.
 * @returns {Promise} axios promise by way of qfetch
 */
export const submitAbandonedGiftDataAPI = ({
    abandonedGiftData,
    formId,
    token,
}) => {
    const url = getSubmitAbandonedGiftDataUrl(formId);
    const data = { details: abandonedGiftData, token };
    return qfetch.post(url, data);
};

/**
 * @description Creates scheduled emails based on form data
 * @param {object} params
 * @param {number} params.amount
 * @param {string} params.embedUrl
 * @param {number} params.formId
 * @param {string} params.recipientEmail
 * @param {string} params.token - The abandoned gift token.
 * @returns {Promise} axios promise by way of qfetch
 */
export const scheduleAbandonedGiftEmailsAPI = ({
    amount,
    embedUrl,
    formId,
    recipientEmail,
    token,
}) => {
    const {
        ENUMS: { MailEvent },
    } = constants;
    const url = getScheduleAbandonedGiftEmailsUrl(formId);
    const data = {
        emailData: { amount, embedUrl, token },
        emailType: MailEvent.ABANDONED_GIFT_REMINDER,
        recipientEmail,
    };
    return qfetch.post(url, data);
};

/**
 * @description Sends gift data to track abandoned gift interactions
 * @param {object} params
 * @param {object} params.abandonedGiftTrackingData
 * @param {number} params.formId
 * @returns {Promise} axios promise by way of qfetch
 */
export const submitAbandonedGiftTrackingData = ({
    abandonedGiftTrackingData,
    formId,
}) => {
    const url = getSubmitAbandonedGiftTrackingDataUrl(formId);
    return qfetch.post(url, abandonedGiftTrackingData);
};

/**
 *
 * @param {object} data
 * @returns {Promise}
 */
export const getDonationIsValid = (data) => {
    const url = getValidateDonationUrl();
    return qfetch.post(url, data);
};
